/**
 * oauth-return-error v1.0 (2026-09-30, cc_tasks row 297, T332)
 *
 * A sign-in with LinkedIn (or Google) that fails comes back from the auth server with
 * ?error=...&error_code=...&error_description=... (the same three again in the #fragment).
 * When the auth server cannot tell where the visitor was going (a lost or expired state)
 * it sends them to the site root, https://mentionfox.com/?error=..., and nothing there
 * read it: the visitor saw the home page and no word that sign-in failed.
 *
 * Now every such return ends on /auth with a plain message and with ?next= set to where
 * the visitor was headed, so pressing the button again brings them back to that page:
 *
 *   /?error=...                  -> /auth?next=<stashed mf_next>&oauth_error=<code>&provider=<p>
 *   /company/x?error=...#error=  -> /auth?next=/company/x&oauth_error=<code>&provider=<p>
 *   /auth?next=/x&error=...      -> /auth?next=/x&oauth_error=<code>&provider=<p>
 *
 * The root case is also redirected by vercel.json (a "/" with ?error_description goes to
 * /auth, query kept) so it works before any script runs; this file then adds next= and
 * the provider, which only the browser knows (the stash written by ./linkedin-signin.ts).
 *
 * Other pages are only rerouted when the return is clearly the auth server's: the error is
 * in the #fragment as well, or this browser started a LinkedIn/Google sign-in in the last
 * 30 minutes. Other OAuth callbacks in the app (connections) keep their own ?error=.
 *
 * The failure is counted once as signup_error "<provider>:<code>" (./signup-funnel.ts).
 * Only the auth server's error code is sent: never the description, never the address.
 */
import { recordSignupError } from "@/lib/signup-funnel";

export type OAuthProvider = "linkedin" | "google" | "unknown";

const PENDING_KEY = "mf_oauth_pending";
const ERROR_KEYS = ["error", "error_code", "error_description"];

export function oauthErrorMessage(provider: string | null | undefined): string {
  if (provider === "linkedin") return "Sign-in with LinkedIn didn't finish. Try again, or use email.";
  if (provider === "google") return "Sign-in with Google didn't finish. Try again, or use email.";
  return "Sign-in didn't finish. Try again, or use email.";
}

function safeInternal(v: string | null | undefined): string | null {
  const s = String(v || "").trim();
  if (!s.startsWith("/") || s.startsWith("//") || s.startsWith("/\\") || /^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  const path = s.split("?")[0].split("#")[0].replace(/\/+$/, "");
  if (path === "/auth") return null; // returning to the login page after logging in is a loop
  return s.slice(0, 500);
}

function cleanCode(v: string | null): string {
  return String(v || "").toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 40);
}

function readPending(): { method: string; at: number } | null {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (p && typeof p.method === "string" && Date.now() - Number(p.at || 0) < 30 * 60 * 1000) return p;
  } catch { /* unreadable: none */ }
  return null;
}

function stashedNext(): string | null {
  try {
    const at = Number(localStorage.getItem("mf_next_at") || "0");
    if (at > 0 && Date.now() - at < 30 * 60 * 1000) return safeInternal(localStorage.getItem("mf_next"));
  } catch { /* ignore */ }
  return null;
}

function providerOf(q: URLSearchParams, h: URLSearchParams, pending: { method: string } | null): OAuthProvider {
  const named = (q.get("provider") || "").toLowerCase();
  if (named === "linkedin" || named === "linkedin_oidc") return "linkedin";
  if (named === "google") return "google";
  if (pending?.method === "linkedin") return "linkedin";
  if (pending?.method === "google") return "google";
  const desc = `${q.get("error_description") || ""} ${h.get("error_description") || ""}`.toLowerCase();
  if (desc.includes("linkedin")) return "linkedin";
  if (desc.includes("google")) return "google";
  return "unknown";
}

/**
 * Run once at startup, before the app renders. Returns true when it sent the browser to
 * /auth (the caller should not render), false otherwise. Never throws.
 */
export function routeOAuthReturnError(): boolean {
  try {
    const loc = window.location;
    const q = new URLSearchParams(loc.search);
    const h = new URLSearchParams(loc.hash.replace(/^#/, ""));
    const inQuery = !!q.get("error") && (q.has("error_description") || q.has("error_code"));
    const inHash = !!h.get("error") && (h.has("error_description") || h.has("error_code"));
    if (!inQuery && !inHash) return false;

    const path = loc.pathname.replace(/\/+$/, "") || "/";
    const pending = readPending();
    // Somebody else's ?error= (an integration callback) is left alone.
    if (path !== "/" && path !== "/auth" && !inHash && !pending) return false;

    const code = cleanCode(q.get("error_code") || h.get("error_code") || q.get("error") || h.get("error")) || "unknown";
    const provider = providerOf(q, h, pending);
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }

    // Where the visitor was going.
    let next: string | null = null;
    if (path === "/auth") {
      next = safeInternal(q.get("next") || q.get("return_to")) || stashedNext();
    } else if (path === "/") {
      next = stashedNext();
    } else {
      const rest = new URLSearchParams(loc.search);
      for (const k of ERROR_KEYS) rest.delete(k);
      const qs = rest.toString();
      next = safeInternal(`${loc.pathname}${qs ? `?${qs}` : ""}`) || stashedNext();
    }

    recordSignupError(provider, code, next);

    // Everything else /auth reads (plan, pack, persona, ref, showwork, signup) is kept.
    const out = path === "/auth" ? new URLSearchParams(loc.search) : new URLSearchParams();
    for (const k of [...ERROR_KEYS, "sb", "oauth_error", "provider", "next", "return_to"]) out.delete(k);
    if (next) out.set("next", next);
    out.set("oauth_error", code);
    out.set("provider", provider);
    const target = `/auth?${out.toString()}`;

    if (path === "/auth") {
      window.history.replaceState(window.history.state, "", target);
      return false;
    }
    loc.replace(target);
    return true;
  } catch {
    return false;
  }
}

/** On /auth: the message to show for a failed LinkedIn/Google return, or null. */
export function oauthErrorNotice(): string | null {
  try {
    const q = new URLSearchParams(window.location.search);
    if (!q.get("oauth_error")) return null;
    return oauthErrorMessage(q.get("provider"));
  } catch {
    return null;
  }
}
